import { useState, useRef } from "react"
import { Turnstile, type TurnstileInstance } from "@marsidev/react-turnstile"
import { OTPInput } from "input-otp"
import clsx from "clsx"
import axios from "axios"
import Modal from "@/components/Modal"
import Spinner from "@/components/Spinner"
import ErrorMessage from "@/components/ErrorMessage"
import { captureEvent } from "@/lib/posthog"

const Slot = ({
  char,
  isActive,
  disabled,
}: {
  char: string | null
  isActive: boolean
  disabled: boolean
}) => (
  <div
    className={clsx(
      "flex size-12 items-center justify-center rounded-xl border border-gray-200 bg-gray-50 text-xl font-bold shadow-inner sm:size-14",
      {
        "!bg-chessdrop-50 outline-chessdrop-400 outline-[3px] outline-offset-0":
          isActive && !disabled,
      }
    )}
  >
    {char !== null && char}
  </div>
)

type Step = "signup" | "login" | "otp"

export default function SignupModal({
  isOpen,
  onClose,
  onSuccess,
}: {
  isOpen: boolean
  onClose: () => void
  onSuccess: () => void
}) {
  const [step, setStep] = useState<Step>("signup")
  const [name, setName] = useState("")
  const [email, setEmail] = useState("")
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [verifying, setVerifying] = useState(false)
  const [resendCooldown, setResendCooldown] = useState(false)
  const turnstileRef = useRef<TurnstileInstance>(null)
  const turnstileSiteKey = import.meta.env.VITE_TURNSTILE_SITE_KEY as string | undefined
  const [turnstileToken, setTurnstileToken] = useState("")

  const handleClose = () => {
    captureEvent("guest_signup_abandoned", { step: step === "otp" ? "otp" : "email" })
    onClose()
  }

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setLoading(true)

    try {
      await axios.post("/api/auth/signup", { name: name.trim(), email: email.trim(), turnstile_token: turnstileToken })
      captureEvent("signup_started", { source: "guest_flow" })
      captureEvent("guest_signup_submitted")
      captureEvent("guest_otp_sent")
      setStep("otp")
    } catch (err: unknown) {
      if (axios.isAxiosError(err) && err.response?.data?.turnstile) {
        captureEvent("turnstile_blocked", {
          reason: err.response.data.turnstile_error,
          endpoint: "/api/auth/signup",
        })
      }
      if (axios.isAxiosError(err) && err.response?.data?.error) {
        setError(err.response.data.error)
      } else {
        setError("Something went wrong. Please try again.")
      }
      turnstileRef.current?.reset()
    } finally {
      setLoading(false)
    }
  }

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setLoading(true)

    try {
      await axios.post("/api/auth/login", { email: email.trim(), turnstile_token: turnstileToken })
      captureEvent("guest_otp_sent")
      setStep("otp")
    } catch (err: unknown) {
      if (axios.isAxiosError(err) && err.response?.data?.turnstile) {
        captureEvent("turnstile_blocked", {
          reason: err.response.data.turnstile_error,
          endpoint: "/api/auth/login",
        })
      }
      setError("Something went wrong. Please try again.")
      turnstileRef.current?.reset()
    } finally {
      setLoading(false)
    }
  }

  const handleVerify = async (otp: string) => {
    setError(null)
    setVerifying(true)

    try {
      await axios.post("/api/auth/verify", { email: email.trim(), code: otp })
      captureEvent("guest_otp_completed")
      onSuccess()
    } catch (err: unknown) {
      if (axios.isAxiosError(err) && err.response?.data?.error) {
        setError(err.response.data.error)
      } else {
        setError("Invalid or expired code.")
      }
    } finally {
      setVerifying(false)
    }
  }

  const handleResend = async () => {
    if (resendCooldown) return
    setError(null)

    try {
      await axios.post("/api/auth/resend", { email: email.trim(), turnstile_token: turnstileToken })
      setResendCooldown(true)
      setTimeout(() => setResendCooldown(false), 30_000)
    } catch (err: unknown) {
      if (axios.isAxiosError(err) && err.response?.data?.turnstile) {
        captureEvent("turnstile_blocked", {
          reason: err.response.data.turnstile_error,
          endpoint: "/api/auth/resend",
        })
      }
      setError("Failed to resend code.")
      turnstileRef.current?.reset()
    }
  }

  const title = step === "otp" ? "Check your inbox" : step === "login" ? "Log in" : "Create account"

  return (
    <Modal show={isOpen} title={title} onClose={handleClose} maxWidthClassName="sm:max-w-md">
      <div className="mt-4">
        {step === "otp" ? (
          <div className="flex flex-col items-center">
            <p className="text-center text-sm text-gray-600">
              We sent a 6-digit code to{" "}
              <span className="font-medium text-gray-900">{email}</span>
            </p>

            <OTPInput
              maxLength={6}
              autoComplete="one-time-code"
              disabled={verifying}
              pushPasswordManagerStrategy="none"
              data-lpignore="true"
              data-1p-ignore="true"
              containerClassName={clsx(
                "group mt-4 flex justify-center gap-x-1 sm:gap-x-2",
                verifying && "opacity-50"
              )}
              onComplete={handleVerify}
              render={({ slots }) =>
                slots.map((slot, idx) => (
                  <Slot key={idx} {...slot} disabled={verifying} />
                ))
              }
            />

            <div className="mt-3 h-6">
              {verifying ? (
                <div className="flex items-center gap-x-2">
                  <span className="text-sm text-gray-600">Verifying</span>
                  <Spinner size="xs" />
                </div>
              ) : (
                <ErrorMessage className="text-center" message={error} />
              )}
            </div>

            <div className="mt-4 flex items-center gap-x-4 text-sm">
              <button
                type="button"
                onClick={handleResend}
                disabled={resendCooldown}
                className={clsx(
                  "font-medium",
                  resendCooldown
                    ? "text-gray-400 cursor-not-allowed"
                    : "text-chessdrop-600 hover:text-chessdrop-500"
                )}
              >
                {resendCooldown ? "Code sent" : "Resend code"}
              </button>
              <button
                type="button"
                onClick={() => { setStep("signup"); setError(null); turnstileRef.current?.reset() }}
                className="text-gray-500 hover:text-gray-700"
              >
                Change email
              </button>
            </div>

          </div>
        ) : (
          <form onSubmit={step === "login" ? handleLogin : handleSignup}>
            {step === "signup" && (
              <div className="mb-3">
                <label htmlFor="signup-name" className="block text-sm font-medium text-gray-700">
                  Name
                </label>
                <input
                  id="signup-name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-gray-900 shadow-sm focus:border-chessdrop-500 focus:ring-chessdrop-500 sm:text-sm"
                />
              </div>
            )}

            <div className="mb-3">
              <label htmlFor="signup-email" className="block text-sm font-medium text-gray-700">
                Email
              </label>
              <input
                id="signup-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-gray-900 shadow-sm focus:border-chessdrop-500 focus:ring-chessdrop-500 sm:text-sm"
              />
            </div>

            <ErrorMessage message={error} />

            {turnstileSiteKey && (
              <Turnstile
                ref={turnstileRef}
                siteKey={turnstileSiteKey}
                options={{
                  action: step === "login" ? "login" : "signup",
                  appearance: "interaction-only",
                }}
                onSuccess={setTurnstileToken}
                className="mt-3"
              />
            )}

            <button
              type="submit"
              disabled={loading}
              className="mt-4 flex w-full items-center justify-center rounded-lg bg-chessdrop-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-chessdrop-500 disabled:opacity-50"
            >
              {loading ? <Spinner size="xs" className="text-white" /> : "Send verification code"}
            </button>

            <div className="mt-4 text-center text-sm text-gray-500">
              {step === "signup" ? (
                <button type="button" onClick={() => { setStep("login"); setError(null); turnstileRef.current?.reset() }} className="text-chessdrop-600 hover:text-chessdrop-500 font-medium">
                  Already have an account? Log in
                </button>
              ) : (
                <button type="button" onClick={() => { setStep("signup"); setError(null); turnstileRef.current?.reset() }} className="text-chessdrop-600 hover:text-chessdrop-500 font-medium">
                  Need an account? Sign up
                </button>
              )}
            </div>
          </form>
        )}
      </div>
    </Modal>
  )
}
